import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'
import { invalidateOnboardingStatus } from '../../hooks/use-onboarding-status'
import { colors } from '../../components/ui/styles'
import medflowLogo from '../../assets/img/MedFlow-Alpha-LIS.png'

type Step = 'welcome' | 'admin' | 'license' | 'review'

interface AdminForm {
  email: string
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
}

interface LicenseForm {
  centralUrl: string
  licenseKey: string
  skip: boolean
}

const STEP_ORDER: Step[] = ['welcome', 'admin', 'license', 'review']

// First-run onboarding wizard. Runs once per deployment, the first time
// the seeded default admin logs in. Produces a new real admin (required)
// and optionally activates a license key against a central hub. On
// submit, the default admin is tombstoned and the user is logged out so
// they can re-authenticate as the new admin.
export function OnboardingPage() {
  const [step, setStep] = useState<Step>('welcome')
  const [admin, setAdmin] = useState<AdminForm>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  })
  const [license, setLicense] = useState<LicenseForm>({ centralUrl: '', licenseKey: '', skip: false })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { logout } = useAuth()
  const navigate = useNavigate()

  function next(): void {
    const i = STEP_ORDER.indexOf(step)
    if (i < STEP_ORDER.length - 1) setStep(STEP_ORDER[i + 1])
  }

  function back(): void {
    const i = STEP_ORDER.indexOf(step)
    if (i > 0) setStep(STEP_ORDER[i - 1])
  }

  function validateAdmin(): string | null {
    if (!admin.email || !admin.firstName || !admin.lastName || !admin.password) return 'All fields are required'
    if (admin.password !== admin.confirmPassword) return 'Passwords do not match'
    if (admin.password.length < 8) return 'Password must be at least 8 characters'
    if (!/[A-Z]/.test(admin.password)) return 'Password must contain an uppercase letter'
    if (!/[a-z]/.test(admin.password)) return 'Password must contain a lowercase letter'
    if (!/[0-9]/.test(admin.password)) return 'Password must contain a number'
    return null
  }

  function handleAdminSubmit(e: FormEvent): void {
    e.preventDefault()
    const err = validateAdmin()
    if (err) {
      setError(err)
      return
    }
    setError(null)
    next()
  }

  function handleLicenseSubmit(e: FormEvent): void {
    e.preventDefault()
    if (!license.skip) {
      if (!license.centralUrl || !license.licenseKey) {
        setError('Central URL and license key are required, or click "Skip for now"')
        return
      }
      try {
        new URL(license.centralUrl)
      } catch {
        setError('Central URL must be a valid URL (e.g. https://fleet.medflow.example.com)')
        return
      }
    }
    setError(null)
    next()
  }

  async function handleConfirm(): Promise<void> {
    setSubmitting(true)
    setError(null)
    try {
      const body = {
        newAdmin: {
          email: admin.email,
          firstName: admin.firstName,
          lastName: admin.lastName,
          password: admin.password,
        },
        licenseActivation: license.skip
          ? null
          : { centralUrl: license.centralUrl.trim(), licenseKey: license.licenseKey.trim() },
      }
      const res = await api.post<{ newAdminEmail: string; licenseActivated: boolean }>('/onboarding/complete', body)
      invalidateOnboardingStatus()
      logout()
      const email = res.data?.newAdminEmail ?? admin.email
      navigate(`/login?onboarded=${encodeURIComponent(email)}`, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to complete onboarding')
    } finally {
      setSubmitting(false)
    }
  }

  const cardStyle = {
    maxWidth: '560px',
    width: '100%',
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '2rem',
  }

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.textMuted,
    marginBottom: '6px',
  }
  const inputStyle = {
    width: '100%',
    height: '40px',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${colors.borderInput}`,
    borderRadius: '3px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
    marginBottom: '1rem',
  }
  const primaryBtn = {
    height: '44px',
    padding: '0 1.5rem',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    textTransform: 'uppercase' as const,
    backgroundColor: colors.primary,
    border: `1px solid ${colors.primary}`,
    borderRadius: '0',
    cursor: 'pointer',
    letterSpacing: '0.75px',
  }
  const secondaryBtn = {
    ...primaryBtn,
    color: colors.textHeading,
    backgroundColor: '#fff',
    border: `1px solid ${colors.border}`,
  }

  return (
    <div
      data-testid="onboarding-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        background: colors.bgPage,
      }}
    >
      <img src={medflowLogo} alt="MedFlow" style={{ width: '280px', marginBottom: '2rem' }} />

      <div style={cardStyle}>
        <h2 style={{ color: colors.textHeading, fontSize: '22px', fontWeight: 600, marginTop: 0 }}>
          First-run Setup ({STEP_ORDER.indexOf(step) + 1} / {STEP_ORDER.length})
        </h2>

        {error && (
          <div
            data-testid="onboarding-error"
            style={{
              color: colors.danger,
              padding: '0.75rem',
              background: colors.bgError,
              border: `1px solid ${colors.borderError}`,
              borderRadius: '3px',
              fontSize: '14px',
              marginBottom: '1rem',
            }}
          >
            {error}
          </div>
        )}

        {step === 'welcome' && (
          <div>
            <p style={{ color: colors.text, lineHeight: 1.6 }}>
              Welcome to MedFlow LIS. Before you can use this deployment, we need to:
            </p>
            <ol style={{ color: colors.text, lineHeight: 1.8 }}>
              <li>Replace the seeded default admin with a real admin account you control.</li>
              <li>
                Optionally activate a license key against your MedFlow central hub, so this deployment shows up in the
                MedFlow fleet dashboard and this instance can display its license status locally. You can skip this step
                and configure it later from Settings &rsaquo; License.
              </li>
            </ol>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button type="button" onClick={next} style={primaryBtn} data-testid="onboarding-welcome-next">
                Get Started
              </button>
            </div>
          </div>
        )}

        {step === 'admin' && (
          <form onSubmit={handleAdminSubmit}>
            <p style={{ color: colors.text, marginTop: 0 }}>Create your admin account.</p>
            <label style={labelStyle}>
              Email
              <input
                type="email"
                required
                value={admin.email}
                onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
                style={inputStyle}
                data-testid="onboarding-email"
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={labelStyle}>
                First name
                <input
                  type="text"
                  required
                  value={admin.firstName}
                  onChange={(e) => setAdmin({ ...admin, firstName: e.target.value })}
                  style={inputStyle}
                  data-testid="onboarding-first-name"
                />
              </label>
              <label style={labelStyle}>
                Last name
                <input
                  type="text"
                  required
                  value={admin.lastName}
                  onChange={(e) => setAdmin({ ...admin, lastName: e.target.value })}
                  style={inputStyle}
                  data-testid="onboarding-last-name"
                />
              </label>
            </div>
            <label style={labelStyle}>
              Password
              <input
                type="password"
                required
                value={admin.password}
                onChange={(e) => setAdmin({ ...admin, password: e.target.value })}
                style={inputStyle}
                data-testid="onboarding-password"
              />
            </label>
            <label style={labelStyle}>
              Confirm password
              <input
                type="password"
                required
                value={admin.confirmPassword}
                onChange={(e) => setAdmin({ ...admin, confirmPassword: e.target.value })}
                style={inputStyle}
                data-testid="onboarding-confirm-password"
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
              <button type="button" onClick={back} style={secondaryBtn}>
                Back
              </button>
              <button type="submit" style={primaryBtn} data-testid="onboarding-admin-next">
                Next
              </button>
            </div>
          </form>
        )}

        {step === 'license' && (
          <form onSubmit={handleLicenseSubmit}>
            <p style={{ color: colors.text, marginTop: 0 }}>
              Paste the license key generated by your customer admin from the MedFlow central hub.
              <br />
              Don&rsquo;t have one yet? You can skip this step and configure it later.
            </p>
            <label style={labelStyle}>
              Central URL
              <input
                type="url"
                placeholder="https://fleet.medflow.example.com"
                value={license.centralUrl}
                onChange={(e) => setLicense({ ...license, centralUrl: e.target.value, skip: false })}
                style={inputStyle}
                data-testid="onboarding-central-url"
              />
            </label>
            <label style={labelStyle}>
              License key
              <input
                type="text"
                value={license.licenseKey}
                onChange={(e) => setLicense({ ...license, licenseKey: e.target.value, skip: false })}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
                data-testid="onboarding-license-key"
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
              <button type="button" onClick={back} style={secondaryBtn}>
                Back
              </button>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setLicense({ centralUrl: '', licenseKey: '', skip: true })
                    setError(null)
                    next()
                  }}
                  style={secondaryBtn}
                  data-testid="onboarding-skip-license"
                >
                  Skip for now
                </button>
                <button type="submit" style={primaryBtn} data-testid="onboarding-license-next">
                  Next
                </button>
              </div>
            </div>
          </form>
        )}

        {step === 'review' && (
          <div>
            <p style={{ color: colors.text, marginTop: 0 }}>Review and confirm.</p>
            <div style={{ background: colors.bgMuted, padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>New admin</div>
              <div style={{ fontSize: '15px', color: colors.textHeading, fontWeight: 500 }}>
                {admin.firstName} {admin.lastName} &lt;{admin.email}&gt;
              </div>
            </div>
            <div style={{ background: colors.bgMuted, padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>License</div>
              <div style={{ fontSize: '15px', color: colors.textHeading, fontWeight: 500 }}>
                {license.skip
                  ? 'Skipped (can be activated later)'
                  : `${license.centralUrl} (key ${license.licenseKey.slice(0, 6)}…)`}
              </div>
            </div>
            <p style={{ color: colors.textMuted, fontSize: '13px' }}>
              After confirming, the seeded default admin will be archived and you will be signed out. Sign back in as
              your new admin account to continue.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
              <button type="button" onClick={back} style={secondaryBtn} disabled={submitting}>
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                style={{ ...primaryBtn, opacity: submitting ? 0.7 : 1 }}
                disabled={submitting}
                data-testid="onboarding-confirm"
              >
                {submitting ? 'Saving...' : 'Confirm & Finish'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
